import { z } from "zod";
import type { AdminSection } from "./admin-navigation";

export type AttentionSeverity = "attention" | "unknown" | "clear";
type Assessment = { severity: AttentionSeverity; detail: string };
export type Observation = {
  data?: unknown;
  isError: boolean;
  isPending: boolean;
  dataUpdatedAt: number;
};

const count = z.number().finite().nonnegative();
const status = z.enum(["healthy", "degraded", "unhealthy"]);
const timestamp = z.string().datetime({ offset: true });
const clear = (detail: string): Assessment => ({ severity: "clear", detail });
const attention = (detail: string): Assessment => ({ severity: "attention", detail });
const unknown = (detail: string): Assessment => ({ severity: "unknown", detail });

function source<T>(
  id: string, label: string, group: string, destination: AdminSection, url: string,
  schema: z.ZodType<T>, assess: (data: T) => Assessment,
  options: { interval?: number; observedAt?: (data: T) => string | null } = {},
) {
  return {
    id, label, group, destination, url,
    interval: options.interval ?? 60_000,
    evaluate(observation: Observation, now: number) {
      let result: Assessment;
      let observedAt: number | null = null;
      const parsed = schema.safeParse(observation.data);
      if (parsed.success) {
        const sourceTime = options.observedAt?.(parsed.data);
        observedAt = options.observedAt
          ? sourceTime ? Date.parse(sourceTime) : null
          : observation.dataUpdatedAt || null;
      }
      // This is an observation-age policy, not a provider incident/SLA threshold.
      const maxAge = (options.interval ?? 60_000) * 3;
      if (observation.isError) result = unknown("Request failed; current state is unknown.");
      else if (observation.isPending) result = unknown("Waiting for an observation.");
      else if (!parsed.success) result = unknown("Source response is incomplete or invalid.");
      else if (observedAt === null || !Number.isFinite(observedAt)) result = unknown("Observation time is unavailable.");
      else if (observedAt > now + 60_000) result = unknown("Observation time is in the future; check the clock.");
      else if (now - observedAt > maxAge) result = unknown("Observation is stale; current state is unknown.");
      else result = assess(parsed.data);
      return { ...result, observedAt, timestampKind: options.observedAt ? "Source checked" : "Received" };
    },
  };
}

export const operationalSources = [
  source("billing", "Paddle operations", "Billing", "billing", "/api/admin/paddle/health",
    z.object({
      environment: z.enum(["sandbox", "live", "unconfigured"]),
      pendingNotifications: count, unboundNotifications: count, pendingOperations: count,
      reconciliationFailures: count, checkedAt: timestamp,
      readiness: z.object({ configurationComplete: z.boolean() }).optional(),
    }), d => {
      const counters = `${d.pendingNotifications} pending notifications, ${d.unboundNotifications} unbound, ${d.pendingOperations} pending operations, ${d.reconciliationFailures} reconciliation failures.`;
      if (d.reconciliationFailures || d.unboundNotifications) return attention(counters);
      if (d.pendingNotifications || d.pendingOperations) return attention(`${counters} Pending does not by itself mean failed.`);
      if (d.environment === "unconfigured" || d.readiness?.configurationComplete === false)
        return attention(`${counters} Configuration is incomplete; review Billing.`);
      return clear(`${counters} No queue attention reported; not launch approval.`);
    }, { observedAt: d => d.checkedAt }),
  source("refunds", "Refund review", "Billing", "billing", "/api/admin/paddle/refund-requests",
    z.object({ requests: z.array(z.object({ state: z.string() })) }), d => {
      const pending = d.requests.filter(r => ["requested", "in_review"].includes(r.state)).length;
      return pending ? attention(`${pending} requests awaiting review in the returned queue.`) : clear("No actionable requests in the returned queue.");
    }),
  source("changes", "Legacy plan-change history", "Billing", "billing", "/api/admin/billing-plan-changes",
    z.array(z.object({ state: z.string() })), d => {
      const pending = d.filter(r => r.state === "needs_attention").length;
      return pending ? attention(`${pending} changes need attention in the latest 100 records.`) : clear("No needs-attention states in the latest 100 records.");
    }),
  source("push", "Web push", "Notifications", "system", "/api/admin/push-health",
    z.object({ ok: z.boolean(), pushServiceStatus: status, checkedAt: timestamp }), d =>
      d.ok && d.pushServiceStatus === "healthy" ? clear("Local VAPID checks passed; delivery is not verified.")
        : attention(`Local push checks report ${d.pushServiceStatus}.`), { observedAt: d => d.checkedAt }),
  source("email", "Email", "Notifications", "system", "/api/admin/email-health",
    z.object({ ok: z.boolean(), status, checkedAt: timestamp }), d =>
      d.ok && d.status === "healthy" ? clear("Connector check passed; inbox delivery is not verified.")
        : attention(`Email connector reports ${d.status}.`), { observedAt: d => d.checkedAt }),
  source("apns", "iOS push", "Notifications", "system", "/api/push/apns-health",
    z.object({ operational: z.boolean() }), d =>
      d.operational ? clear("APNs initialized; device delivery is not verified.") : attention("APNs is not operational.")),
  source("sms", "SMS configuration", "Notifications", "system", "/api/admin/twilio-status",
    z.object({ configured: z.boolean() }), d =>
      d.configured ? clear("Sender configured; carrier delivery is not verified.") : attention("SMS sender is not configured.")),
  source("metrics", "Delivery and API quotas", "Quotas & delivery", "system", "/api/metrics",
    z.object({
      openweather: z.object({ remainingCalls: z.number().finite(), requestsToday: count, dailyLimit: count }),
      pushNotifications: z.object({ failedToday: count }),
      requests: z.object({ rateLimit: count }),
      windy: z.object({ status: z.enum(["healthy", "degraded", "unknown"]), rateLimits: count }).optional(),
    }), d => {
      const detail = `OpenWeather ${d.openweather.requestsToday}/${d.openweather.dailyLimit} today; ${d.pushNotifications.failedToday} push failures; ${d.requests.rateLimit} API rate limits${d.windy ? `; ${d.windy.rateLimits} Windy rate limits` : ""}.`;
      return d.openweather.remainingCalls <= 0 || d.pushNotifications.failedToday > 0 || d.requests.rateLimit > 0 || (d.windy?.rateLimits ?? 0) > 0
        ? attention(detail) : clear(detail);
    }),
  source("forecast", "Projected weather quota", "Quotas & delivery", "system", "/api/admin/usage-forecast",
    z.object({ estimatedCallsPerDay: count, dailyLimit: count, quotaExceededAt: z.string().nullable() }), d => {
      const detail = `Projected ${d.estimatedCallsPerDay}/${d.dailyLimit} calls per day. Estimate, not provider balance.`;
      return d.estimatedCallsPerDay >= d.dailyLimit || d.quotaExceededAt ? attention(detail) : clear(detail);
    }),
  source("health", "Application health", "Application errors", "system", "/api/health",
    z.object({ status, timestamp }), d =>
      d.status === "healthy" ? clear("Application health check reports healthy.") : attention(`Application health reports ${d.status}.`),
    { interval: 30_000, observedAt: d => d.timestamp }),
  source("errors", "Local error logs", "Application errors", "errors", "/api/admin/error-stats",
    z.object({ last24Hours: count }), d =>
      d.last24Hours ? attention(`${d.last24Hours} retained log entries in the last 24 hours.`) : clear("No retained entries in the last 24 hours; process-local history only.")),
  source("sentry", "Sentry issues", "Application errors", "system", "/api/admin/sentry-error-count",
    z.object({ configured: z.boolean(), count: count.nullable(), capped: z.boolean(), cachedAt: timestamp.nullable() }), d => {
      if (!d.configured || d.count === null) return unknown("Sentry count is not configured or unavailable.");
      return d.count > 0 ? attention(`${d.capped ? "100+" : d.count} new unresolved issues first seen in the last 24 hours.`)
        : clear("No new unresolved issues first seen in the last 24 hours.");
    }, { interval: 300_000, observedAt: d => d.cachedAt }),
];

export function observationLabel(time: number | null) {
  return time !== null && Number.isFinite(time) ? new Date(time).toLocaleString() : "not available";
}
